import apiClient from './client';
import type {
  Receipt,
  ReceiptItem,
  ReceiptFilters,
  ReceiptUpdateRequest,
  PaginatedResponse,
  Category,
  Store,
  Tag,
  Budget,
  BudgetCreateRequest,
} from '../types';
import { cacheReceipts, getCachedReceipts } from '../lib/offlineDb';

// =============================================
// Receipts
// =============================================
export const receiptsApi = {
  list: async (filters?: ReceiptFilters): Promise<PaginatedResponse<Receipt>> => {
    const params = filters ? { ...filters } : {};
    try {
      const response = await apiClient.get<PaginatedResponse<Receipt>>('/receipts', { params });
      // Cache erfolgreich abgerufene Belege fuer Offline-Nutzung
      if (response.data.items && response.data.items.length > 0) {
        cacheReceipts(response.data.items).catch(() => {
          // Cache-Fehler sollen die UI nicht blockieren
        });
      }
      return response.data;
    } catch (error) {
      // Offline-Fallback: gecachte Daten aus IndexedDB zurueckgeben
      if (!navigator.onLine) {
        const cachedItems = await getCachedReceipts();
        return {
          items: cachedItems,
          total: cachedItems.length,
          page: 1,
          page_size: cachedItems.length,
          pages: 1,
        };
      }
      throw error;
    }
  },

  get: async (id: string): Promise<Receipt> => {
    const response = await apiClient.get<Receipt>(`/receipts/${id}`);
    return response.data;
  },

  upload: async (file: File, metadata?: {
    store_name?: string;
    total_amount?: number;
    currency?: string;
    receipt_date?: string;
  }): Promise<Receipt> => {
    const formData = new FormData();
    formData.append('file', file);
    if (metadata?.store_name) formData.append('store_name', metadata.store_name);
    if (metadata?.total_amount != null) formData.append('total_amount', metadata.total_amount.toString());
    if (metadata?.currency) formData.append('currency', metadata.currency);
    if (metadata?.receipt_date) formData.append('receipt_date', metadata.receipt_date);

    const response = await apiClient.post<Receipt>('/receipts', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  /**
   * Upload 1-5 photos of a single long receipt. Files MUST be in display
   * order (top of receipt → bottom). Backend sends all images together to
   * Gemini Vision so it can resolve overlaps and merge items into one entry.
   */
  uploadMulti: async (files: File[], metadata?: {
    store_name?: string;
    total_amount?: number;
    currency?: string;
    receipt_date?: string;
  }): Promise<Receipt> => {
    const formData = new FormData();
    for (const f of files) {
      formData.append('files', f);
    }
    if (metadata?.store_name) formData.append('store_name', metadata.store_name);
    if (metadata?.total_amount != null) formData.append('total_amount', metadata.total_amount.toString());
    if (metadata?.currency) formData.append('currency', metadata.currency);
    if (metadata?.receipt_date) formData.append('receipt_date', metadata.receipt_date);

    const response = await apiClient.post<Receipt>('/receipts/multi', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  update: async (id: string, data: ReceiptUpdateRequest): Promise<Receipt> => {
    const response = await apiClient.put<Receipt>(`/receipts/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/receipts/${id}`);
  },

  getImageUrl: (id: string, size: 'original' | 'thumb' = 'original', index = 0): string => {
    return `/api/v1/receipts/${id}/image?size=${size}&index=${index}`;
  },

  getImage: (id: string, size: 'original' | 'thumb' = 'original', index = 0): string => {
    return `/api/v1/receipts/${id}/image?size=${size}&index=${index}`;
  },

  /** Fetch the receipt image as a Blob (uses auth headers via apiClient).
   *  Pass `index` for multi-image receipts (0 = first/primary). */
  fetchImageBlob: async (
    id: string,
    size: 'original' | 'thumb' = 'original',
    index = 0,
  ): Promise<Blob> => {
    const response = await apiClient.get(`/receipts/${id}/image`, {
      params: { size, index },
      responseType: 'blob',
    });
    return response.data;
  },
};

// =============================================
// Receipt Items
// =============================================
export const receiptItemsApi = {
  list: async (receiptId: string): Promise<ReceiptItem[]> => {
    const response = await apiClient.get<ReceiptItem[]>(
      `/receipts/${receiptId}/items`
    );
    return response.data;
  },

  update: async (
    receiptId: string,
    itemId: string,
    data: Partial<ReceiptItem>
  ): Promise<ReceiptItem> => {
    const response = await apiClient.patch<ReceiptItem>(
      `/receipts/${receiptId}/items/${itemId}`,
      data
    );
    return response.data;
  },

  delete: async (receiptId: string, itemId: string): Promise<void> => {
    await apiClient.delete(`/receipts/${receiptId}/items/${itemId}`);
  },
};

// =============================================
// Categories
// =============================================
export const categoriesApi = {
  list: async (): Promise<Category[]> => {
    const response = await apiClient.get<Category[]>('/categories');
    return response.data;
  },

  create: async (data: Omit<Category, 'id' | 'created_at'>): Promise<Category> => {
    const response = await apiClient.post<Category>('/categories', data);
    return response.data;
  },

  update: async (id: string, data: Partial<Category>): Promise<Category> => {
    const response = await apiClient.patch<Category>(`/categories/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/categories/${id}`);
  },
};

// =============================================
// Stores
// =============================================
export const storesApi = {
  list: async (): Promise<Store[]> => {
    const response = await apiClient.get<Store[]>('/stores');
    return response.data;
  },

  get: async (id: string): Promise<Store> => {
    const response = await apiClient.get<Store>(`/stores/${id}`);
    return response.data;
  },
};

// =============================================
// Tags
// =============================================
export const tagsApi = {
  list: async (): Promise<Tag[]> => {
    const response = await apiClient.get<Tag[]>('/tags');
    return response.data;
  },

  create: async (name: string): Promise<Tag> => {
    const response = await apiClient.post<Tag>('/tags', { name });
    return response.data;
  },

  update: async (id: string, name: string): Promise<Tag> => {
    const response = await apiClient.put<Tag>(`/tags/${id}`, { name });
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/tags/${id}`);
  },

  attachToReceipt: async (receiptId: string, tagId: string): Promise<void> => {
    await apiClient.post(`/receipts/${receiptId}/tags/${tagId}`);
  },

  detachFromReceipt: async (receiptId: string, tagId: string): Promise<void> => {
    await apiClient.delete(`/receipts/${receiptId}/tags/${tagId}`);
  },
};

// =============================================
// Budgets
// =============================================
export const budgetsApi = {
  list: async (): Promise<Budget[]> => {
    const response = await apiClient.get<Budget[]>('/budgets');
    return response.data;
  },

  create: async (data: BudgetCreateRequest): Promise<Budget> => {
    const response = await apiClient.post<Budget>('/budgets', data);
    return response.data;
  },

  update: async (id: string, data: Partial<BudgetCreateRequest>): Promise<Budget> => {
    const response = await apiClient.patch<Budget>(`/budgets/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/budgets/${id}`);
  },
};
